import { createApi, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { fetchAllPages, FETCH_ALL_PAGE_SIZE } from '@/lib/fetchAllPages';
import { services } from '@config/services';
import { replaceShippingMappingOverrides, type ReplaceShippingMappingOverridesArgs } from './replaceShippingMappingOverrides';
import type {
  ShippingMappingOverrideItem,
  ShippingMappingOverridesArgs,
  ShippingMappingOverridesBulkBody,
  ShippingMappingOverridesBulkResponse,
  ShippingMappingOverridesBulkRowResult,
  ShippingMappingOverridesResponse,
  VendorShippingMappingOverridesArgs,
} from './types';

/** What an upload hands the replacement; the writes it makes are the mutation's own. */
export type ReplaceShippingMappingOverridesMutationArgs = Pick<
  ReplaceShippingMappingOverridesArgs,
  'merchantId' | 'existing' | 'body'
>;

/**
 * Merchant shipping-mapping-overrides slice. Drives the
 * ConnectionDetails > Shipping Mapping sub-tab (Phase 4 of the
 * Plugins port).
 *
 * Live wire shapes (verified 2026-05-28 as super-admin viewing
 * Sales Channel 1 on the Julia A Vendor Plugin connection details):
 *
 *   GET /merchant/v1/shipping-mapping-overrides
 *     ?merchantId=<uuid>&vendorId=<uuid>&page=<n>&pageSize=250
 *     → { items: ShippingMappingOverrideItem[], total }
 *     (`&ownOnly=true` from the Shipping Mapping tab: a sub-account's list then never
 *     carries its parent's records, which the tab would otherwise edit and delete)
 *
 *   POST|PUT /merchant/v1/shipping-mapping-overrides/bulk
 *     body: { bulk: ShippingMappingOverrideItem[] } (at most 10 items)
 *     → { bulk: [{ status, response }, ...] } (per-row)
 *
 *   DELETE /merchant/v1/shipping-mapping-overrides/{id}
 *     (no bulk delete; `replaceShippingMappingOverrides` deletes the
 *     overrides an upload leaves out one at a time)
 */

function overridesPageUrl(
  { merchantId, vendorId, facilityId, ownOnly }: ShippingMappingOverridesArgs,
  page: number,
): string {
  const params = new URLSearchParams();
  params.set('merchantId', merchantId);
  params.set('vendorId', vendorId);
  if (facilityId) params.set('facilityId', facilityId);
  if (ownOnly) params.set('ownOnly', 'true');
  params.set('page', String(page));
  params.set('pageSize', String(FETCH_ALL_PAGE_SIZE));
  return `${services.merchant}/v1/shipping-mapping-overrides?${params.toString()}`;
}

function bulkUrl(): string {
  return `${services.merchant}/v1/shipping-mapping-overrides/bulk`;
}

function overrideUrl(id: string): string {
  return `${services.merchant}/v1/shipping-mapping-overrides/${encodeURIComponent(id)}`;
}

function vendorOverridesPageUrl(
  { merchantId, merchantPluginId, facilityId }: VendorShippingMappingOverridesArgs,
  page: number,
): string {
  const params = new URLSearchParams();
  params.set('merchantId', merchantId);
  params.set('merchantPluginId', merchantPluginId);
  if (facilityId) params.set('facilityId', facilityId);
  params.set('page', String(page));
  params.set('pageSize', String(FETCH_ALL_PAGE_SIZE));
  return `${services.merchant}/v1/vendorShippingMappingOverrides?${params.toString()}`;
}

function vendorBulkUrl(merchantPluginId: string): string {
  return `${services.merchant}/v1/vendorShippingMappingOverrides/bulk?merchantPluginId=${encodeURIComponent(merchantPluginId)}`;
}

function vendorOverridesTag(merchantPluginId: string) {
  return { type: 'ShippingMappingOverride' as const, id: `vendor:${merchantPluginId}` };
}

function vendorOverrideUrl(id: string, merchantPluginId: string): string {
  return `${services.merchant}/v1/vendorShippingMappingOverrides/${encodeURIComponent(id)}?merchantPluginId=${encodeURIComponent(merchantPluginId)}`;
}

/**
 * Carries a failed request out of a helper that reports a failure by rejecting: the page
 * reads of `fetchAllPages`, and the writes of a replacement.
 */
class RequestFailed extends Error {
  constructor(readonly queryError: FetchBaseQueryError) {
    super('A shipping mapping overrides request failed');
  }
}

/**
 * The server pages without a sort, so a write between two page reads can show a row on a
 * later page as well, and it is kept once. The same write can instead move a row onto a page
 * already read, where it is missed. A write made here invalidates this list, so a read it
 * tore lasts only until the refetch it triggers; one made elsewhere, until the next load.
 */
function uniqueById(items: ShippingMappingOverrideItem[]): ShippingMappingOverrideItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item.id) return true;
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

type FetchWithBQ = (request: FetchArgs) => PromiseLike<BaseQueryResult> | BaseQueryResult;
type BaseQueryResult = { data?: unknown; error?: FetchBaseQueryError };
type Send = <T>(request: FetchArgs) => Promise<T>;

function sender(fetchWithBQ: FetchWithBQ): Send {
  return async <T>(request: FetchArgs): Promise<T> => {
    const res = await fetchWithBQ(request);
    if (res.error) throw new RequestFailed(res.error);
    return res.data as T;
  };
}

async function settle<T>(run: () => Promise<T>): Promise<{ data: T } | { error: FetchBaseQueryError }> {
  try {
    return { data: await run() };
  } catch (err) {
    if (err instanceof RequestFailed) return { error: err.queryError };
    throw err;
  }
}

/** Any page failing fails the read: an incomplete list must not pass for a whole one. */
async function readEveryPage(send: Send, pageUrl: (page: number) => string): Promise<ShippingMappingOverridesResponse> {
  const items = await fetchAllPages<ShippingMappingOverrideItem>({
    fetchPage: async (page) => {
      const body = await send<Partial<ShippingMappingOverridesResponse> | undefined>({ url: pageUrl(page), method: 'GET' });
      return { items: body?.items ?? [], total: body?.total ?? 0 };
    },
  });
  const unique = uniqueById(items);
  return { items: unique, total: unique.length };
}

/**
 * merchant-service has no vendor bulk PUT, so each update goes on its own, one at a time, and
 * answers as a bulk row would: one the server refuses is a rejected row. A failure the server
 * did not answer for the row fails the replacement, as a failed bulk request does.
 */
async function updateEachForVendor(
  fetchWithBQ: FetchWithBQ,
  merchantPluginId: string,
  body: ShippingMappingOverridesBulkBody,
): Promise<ShippingMappingOverridesBulkResponse> {
  const bulk: ShippingMappingOverridesBulkRowResult[] = [];
  for (const item of body.bulk) {
    const res = await fetchWithBQ({ url: vendorOverrideUrl(item.id ?? '', merchantPluginId), method: 'PUT', body: item });
    if (!res.error) {
      bulk.push({ status: 200 });
      continue;
    }
    const { status } = res.error;
    if (typeof status !== 'number' || status >= 500) throw new RequestFailed(res.error);
    bulk.push({ status, response: res.error.data as ShippingMappingOverridesBulkRowResult['response'] });
  }
  return { bulk };
}

export const merchantShippingMappingsApi = createApi({
  reducerPath: 'merchantShippingMappingsApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['ShippingMappingOverride'],
  endpoints: (builder) => ({
    /**
     * Live URL captured 2026-05-28:
     *   GET /merchant/v1/shipping-mapping-overrides
     *     ?merchantId=f8bebda7-…&vendorId=ee667059-…&page=1&pageSize=250
     *
     * `facilityId` is optional — the legacy service helper appends it
     * when provided. Param order is preserved verbatim from the
     * legacy `getShippingMappingOverride` helper so the wire matches.
     *
     * Every page, not only the first: the Shipping Mapping tab replaces, reverts and
     * exports exactly the list it is given, so a plugin with more overrides than one
     * page holds would keep the unloaded ones beside an upload's new rows. 250 is the
     * largest page the server serves.
     */
    getShippingMappingOverrides: builder.query<
      ShippingMappingOverridesResponse,
      ShippingMappingOverridesArgs
    >({
      queryFn: (args, _api, _extra, fetchWithBQ) =>
        settle(() => readEveryPage(sender(fetchWithBQ), (page) => overridesPageUrl(args, page))),
      providesTags: (_r, _e, { merchantId, vendorId }) => [
        { type: 'ShippingMappingOverride', id: `${merchantId}:${vendorId}` },
      ],
    }),

    /**
     * Bulk create — POST /merchant/v1/shipping-mapping-overrides/bulk
     * Body shape: { bulk: ShippingMappingOverrideItem[] }
     * Response shape: { bulk: [{ status, response }, ...] }
     *
     * The bulk endpoint does NOT delete existing rows; callers that
     * want replace semantics use `replaceShippingMappingOverrides`.
     */
    createShippingMappingOverridesBulk: builder.mutation<
      ShippingMappingOverridesBulkResponse,
      ShippingMappingOverridesBulkBody
    >({
      query: (body) => ({
        url: bulkUrl(),
        method: 'POST',
        body,
      }),
      // Match the query's provided tag (`${merchantId}:${vendorId}`) — a
      // type-level invalidation covers every cached merchant:vendor entry.
      // The previous `id: 'LIST'` matched nothing the query provided, so the
      // overrides list went stale after create/delete.
      invalidatesTags: ['ShippingMappingOverride'],
    }),

    /**
     * An upload's whole replacement (see `replaceShippingMappingOverrides`) as one mutation,
     * so the list is invalidated once, when it settles. Its bulk POSTs and PUTs and its
     * DELETEs go straight to the base query: as mutations of their own, each batch
     * invalidated the list as it settled, so a large upload re-read every page about once per
     * five deletes and swapped the tab for its loading card each time. A failed request still
     * invalidates the list, since the writes before it landed.
     */
    replaceShippingMappingOverrides: builder.mutation<
      ShippingMappingOverridesBulkResponse,
      ReplaceShippingMappingOverridesMutationArgs
    >({
      queryFn: (args, _api, _extra, fetchWithBQ) => {
        const send = sender(fetchWithBQ);
        return settle(() =>
          replaceShippingMappingOverrides({
            ...args,
            createBulk: (body) => send({ url: bulkUrl(), method: 'POST', body }),
            updateBulk: (body) => send({ url: bulkUrl(), method: 'PUT', body }),
            deleteOne: (id) => send({ url: overrideUrl(id), method: 'DELETE' }),
          }),
        );
      },
      invalidatesTags: ['ShippingMappingOverride'],
    }),

    /**
     * Delete one override row. There is no bulk-delete endpoint on the
     * server; an upload deletes the rows it leaves out one at a time
     * inside `replaceShippingMappingOverrides`.
     */
    deleteShippingMappingOverride: builder.mutation<void, string>({
      query: (id) => ({
        url: overrideUrl(id),
        method: 'DELETE',
      }),
      // Match the query's provided tag (`${merchantId}:${vendorId}`) — a
      // type-level invalidation covers every cached merchant:vendor entry.
      // The previous `id: 'LIST'` matched nothing the query provided, so the
      // overrides list went stale after create/delete.
      invalidatesTags: ['ShippingMappingOverride'],
    }),

    /**
     * Update one override in place — atomic single call.
     *
     * Live wire shape (verified against merchant-service controller):
     *   PUT /merchant/v1/shipping-mapping-overrides
     *   body: { id, merchantId, vendorId, facilityId, shippingGroupId, shippingMappings }
     *   → ShippingMappingOverridesDto (200)
     *
     * Used by handleGroupChange to change shippingGroupId without the
     * data-loss risk of delete-then-create.
     */
    updateShippingMappingOverride: builder.mutation<
      ShippingMappingOverridesResponse,
      ShippingMappingOverrideItem & { id: string }
    >({
      query: (item) => ({
        url: `${services.merchant}/v1/shipping-mapping-overrides`,
        method: 'PUT',
        body: item,
      }),
      invalidatesTags: ['ShippingMappingOverride'],
    }),

    getVendorShippingMappingOverrides: builder.query<
      ShippingMappingOverridesResponse,
      VendorShippingMappingOverridesArgs
    >({
      queryFn: (args, _api, _extra, fetchWithBQ) =>
        settle(() => readEveryPage(sender(fetchWithBQ), (page) => vendorOverridesPageUrl(args, page))),
      providesTags: (_r, _e, { merchantPluginId }) => [vendorOverridesTag(merchantPluginId)],
    }),

    createVendorShippingMappingOverridesBulk: builder.mutation<
      ShippingMappingOverridesBulkResponse,
      { merchantPluginId: string; body: ShippingMappingOverridesBulkBody }
    >({
      query: ({ merchantPluginId, body }) => ({
        url: vendorBulkUrl(merchantPluginId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { merchantPluginId }) => [vendorOverridesTag(merchantPluginId)],
    }),

    replaceVendorShippingMappingOverrides: builder.mutation<
      ShippingMappingOverridesBulkResponse,
      ReplaceShippingMappingOverridesMutationArgs & { merchantPluginId: string }
    >({
      queryFn: ({ merchantPluginId, ...args }, _api, _extra, fetchWithBQ) => {
        const send = sender(fetchWithBQ);
        return settle(() =>
          replaceShippingMappingOverrides({
            ...args,
            createBulk: (body) => send({ url: vendorBulkUrl(merchantPluginId), method: 'POST', body }),
            updateBulk: (body) => updateEachForVendor(fetchWithBQ, merchantPluginId, body),
            deleteOne: (id) => send({ url: vendorOverrideUrl(id, merchantPluginId), method: 'DELETE' }),
          }),
        );
      },
      invalidatesTags: (_r, _e, { merchantPluginId }) => [vendorOverridesTag(merchantPluginId)],
    }),

    updateVendorShippingMappingOverride: builder.mutation<
      ShippingMappingOverridesResponse,
      { merchantPluginId: string; item: ShippingMappingOverrideItem & { id: string } }
    >({
      query: ({ merchantPluginId, item }) => ({
        url: vendorOverrideUrl(item.id, merchantPluginId),
        method: 'PUT',
        body: item,
      }),
      invalidatesTags: (_r, _e, { merchantPluginId }) => [vendorOverridesTag(merchantPluginId)],
    }),

    deleteVendorShippingMappingOverride: builder.mutation<void, { merchantPluginId: string; id: string }>({
      query: ({ merchantPluginId, id }) => ({
        url: vendorOverrideUrl(id, merchantPluginId),
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { merchantPluginId }) => [vendorOverridesTag(merchantPluginId)],
    }),
  }),
});

export const {
  useGetShippingMappingOverridesQuery,
  useCreateShippingMappingOverridesBulkMutation,
  useReplaceShippingMappingOverridesMutation,
  useDeleteShippingMappingOverrideMutation,
  useUpdateShippingMappingOverrideMutation,
  useGetVendorShippingMappingOverridesQuery,
  useCreateVendorShippingMappingOverridesBulkMutation,
  useReplaceVendorShippingMappingOverridesMutation,
  useUpdateVendorShippingMappingOverrideMutation,
  useDeleteVendorShippingMappingOverrideMutation,
} = merchantShippingMappingsApi;
